import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  normalizeTask,
  normalizeProject,
  phaseStatusKey,
  memberRoleLabel,
  fmtInputDate,
  daysBetween,
  PRIO_LABEL,
  PROJ_STATUS_LABEL,
  MEMBER_ROLE_CODE,
  toPlainText,
} from "../../design/domain";
import { useApp } from "../../design/AppContext";
import EntityForm from "../../components/design/EntityForm";
import DetailSummary from "./DetailSummary";
import DetailItems from "./DetailItems";
import DetailData from "./DetailData";
import { formConfig, FORM_TITLES, FORM_EDIT_TITLES, formInitialValues } from "../../resources/forms";
import { apiDelete, apiGet, currentAccount } from "../../utils/api";
import { apiErrorMessage } from "../../utils/apiError";
import { hasPermission } from "../../utils/permission";
import { jwtEncode } from "../../utils/jwt";
import request from "../../utils/request";
import { getToken } from "../../utils/token";
import {
  MdCheck,
  MdChevronRight,
  MdCircle,
  MdErrorOutline,
  MdExpandLess,
  MdExpandMore,
  MdMoreHoriz,
  MdRemove,
  MdWarningAmber,
} from "react-icons/md";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { Banner, StatusChip } from "../../components/mui";
import { InlineLoading } from "../../components/loading";

/**
 * 專案詳情（設計稿 #screen-project L758-L860 與 #screen-project-data L1286-L1394）
 *
 * 一個路由、一個五顆分頁共用的頁首、五顆分頁：
 *   · 頁首 ＝ 標題＋狀態（同一行最右邊是「打開看板」）／日期那一行／「⋯ 更多」。
 *     「更多」原地展開成摘要卡（DetailSummary：進度、專案資訊、新增工項、編輯與刪除專案）
 *   · 工項（預設）＝ 工項清單（DetailItems，清單下面有自己的「＋ 新增工項」）
 *   · 里程碑／成員／文件／會議紀錄（?tab=phases / members / docs / meetings）
 *     ＝ 各自一顆分頁，內容由 DetailData 依 section 只畫那一區
 *   · 看板不是分頁，是另一頁（/Board）：入口在頁首右上角，不在分頁列裡
 *
 * 頁首為什麼不再跟著分頁變：
 *   以前工項分頁上面是一整張摘要卡，其他四顆只有一顆「⋯ 更多」。一切分頁，底下的分頁列
 *   就上下跳，工項分頁看起來像另一頁。現在五顆的頁首一字不差，摘要卡收進「更多」，
 *   要看的人自己展開；分頁列上面永遠是同樣的三行，切分頁只換分頁列下面的內容。
 *
 * 分頁為什麼又從 3 顆變回 6 顆（後來看板移到頁首，剩 5 顆）：
 *   設計稿 L791 把里程碑、文件、成員、會議紀錄收進同一顆分頁、裡面是四個收合區，
 *   理由是四種東西加起來一個專案也才二三十筆。上線後使用者反映那一顆的名字是個籠統的
 *   總稱，會誤導 —— 看不出成員、會議紀錄在裡面，找到了還要再點一次展開。
 *   現在筆數直接掛在分頁上，哪一區有東西一眼就看得到，「一格一格點過去找」的顧慮
 *   已經不成立；四區升回同級分頁，一點就是內容，不再有收合那一層。
 *
 * 這一版拿掉的東西與理由：
 *   · 頁首的數字牆與「專案資訊」彈窗 → 摘要卡的整句敘述（見 DetailSummary）。
 *   · 工項分頁的狀態膠囊／8 種排序／卡片列表雙檢視／7 欄表格 → 兩個白話下拉 ＋ 清單列（RowButton）
 *     （被收掉的常數留在 DetailItems.js 底下，見那裡的說明）。
 *   · 工項詳情不再是頁內面板：?task= 改成導到 /Task/:id 整頁（見下面的 effect），
 *     所以工項的新增／編輯／刪除也都在 /Task/New 與 /Task/:id，不在這一頁重做一套。
 *
 * 版面規則：外層不另外包頁面容器，Layout.js 的內容區本身就帶內距與寬度上限。
 */

/* ===================== 網址參數 ===================== */

/**
 * 分頁鍵；也是 ?tab= 的合法值，其他值一律當作沒帶。
 * 後四個的字串刻意沿用最早五個分頁時代的鍵（phases / docs / members / meetings）：
 * 那一代的書籤與貼出去的連結不必轉換就直接落在對的分頁，
 * DetailData 的 section 也收同一組字串，兩邊不用再對照一張表。
 */
const TAB_ITEMS = "items";
/* 看板不是這一頁的分頁（它是 /Board），分頁列裡也已經沒有它。
   鍵還留著只為了認得舊連結的 ?tab=board —— 下面的 effect 會把那種連結直接送去看板頁 */
const TAB_BOARD = "board";
const TAB_PHASES = "phases";
const TAB_MEMBERS = "members";
const TAB_DOCS = "docs";
const TAB_MEETINGS = "meetings";
const TAB_KEYS = [TAB_ITEMS, TAB_BOARD, TAB_PHASES, TAB_MEMBERS, TAB_DOCS, TAB_MEETINGS];

/*
 * 已經不存在的分頁鍵 → 現在該落的分頁。只剩一個：
 * 上一版四區併在同一顆分頁時用的是 ?tab=data。那一版打開時第一個（也是唯一預設展開的）
 * 區塊就是里程碑，所以落到里程碑 —— 使用者從舊連結點進來看到的第一眼跟以前一樣。
 * 下面的 effect 會順手把網址改寫成新鍵，之後複製出去的連結就不再帶舊值。
 */
const TAB_ALIASES = { data: TAB_PHASES };

/*
 * 舊網址相容。
 *
 * 這一頁以前把 status / sort / dir / view / cat / who / q / page 全部寫進網址，
 * 現在篩選與排序都只留在元件 state 裡（切一下就推一筆歷史紀錄的話，返回鍵要按五次
 * 才離得開專案）。**靜默忽略**而不是報錯：站內深連結（命令面板、通知、搜尋、Map、
 * 儀表板）帶的是 ?task=，沒有人在產生這四個參數，但使用者的書籤與貼給同事的舊連結
 * 一定還帶著 —— 那種連結現在會落在預設的工項視圖，是可以接受的降級；
 * 反過來若在這裡擋下來或報錯，就是把一條本來走得通的路變成死路。
 *
 * 只有 tab 與 task 兩個參數還有意義，其餘一概不讀。
 */
const LEGACY_PARAMS = ["status", "sort", "dir", "view", "cat", "who", "q", "page"];

/**
 * 舊鍵 → 新鍵；不是舊鍵就回 null。
 * 用 hasOwnProperty 而不是直接 TAB_ALIASES[raw]：?tab=constructor 這種值會撈到
 * Object 原型上的函式，下面改寫網址時就會把一整段函式原始碼塞進 ?tab=。
 */
function tabAlias(raw) {
  return raw && Object.prototype.hasOwnProperty.call(TAB_ALIASES, raw) ? TAB_ALIASES[raw] : null;
}

function parseListQuery(sp) {
  const raw = sp.get("tab");
  const tab = tabAlias(raw) || raw;
  return { tab: TAB_KEYS.includes(tab) ? tab : TAB_ITEMS };
}

/* ===================== 日期與格式 ===================== */

/**
 * 「今天」一律用當地時區的日界判斷（同 /Today、/Tasks）。
 * 後端的 due_date 是 DateTimeOffset，直接比 UTC 的話，台北時間今天早上 7 點以前
 * 到期的工項會被算成昨天。
 */
function isSameDay(v, base) {
  if (!v) return false;
  const d = dayjs(v);
  return d.isValid() && d.isSame(base, "day");
}

/** 日期文案：今年寫「9 月 20 日」，跨年才補年份；沒有日期就回空字串（不要填「—」充版面） */
function fmtDay(v) {
  if (!v) return "";
  const d = dayjs(v);
  if (!d.isValid()) return "";
  return d.isSame(dayjs(), "year") ? d.format("M 月 D 日") : d.format("YYYY 年 M 月 D 日");
}

/** 會議時間：午夜（只填日期沒填時間）就不印 00:00，那是假的精確度 */
function fmtMoment(v) {
  if (!v) return "";
  const d = dayjs(v);
  if (!d.isValid()) return "";
  const day = fmtDay(v);
  return d.hour() === 0 && d.minute() === 0 ? day : `${day} ${d.format("HH:mm")}`;
}

/** 檔案大小（設計稿 L1333 的「2.4 MB」） */
function fmtSize(bytes) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${parseFloat((bytes / 1024 ** i).toFixed(1))} ${units[i] || "B"}`;
}

/**
 * 專案這一刻最該被講出來的一句話 → 標題旁的 StatusChip（同 /Project/List 的 tagOf）。
 * 期限一律搶在狀態前面：一列（一個標題）只能講一件事，「進行中」跟「今天到期」
 * 同時出現的話，真正要看的那個會被稀釋掉。
 */
function projectTag(p, today) {
  const end = p.endRaw ? dayjs(p.endRaw) : null;
  const live = p.status !== "done" && p.status !== "cancelled";
  if (live && end && end.isValid()) {
    const diff = end.startOf("day").diff(today, "day");
    if (diff === 0) return { kind: "attention", mark: <MdWarningAmber />, text: "今天到期" };
    if (diff < 0) return { kind: "attention", mark: <MdWarningAmber />, text: "已逾期" };
  }
  switch (p.status) {
    case "done":
      return { kind: "done", mark: <MdCheck />, text: "已完成" };
    case "progress":
      return { kind: "progress", mark: <MdCircle size="0.6em" />, text: "進行中" };
    case "paused":
      return { kind: "warn", mark: <MdErrorOutline />, text: "暫停" };
    case "cancelled":
      return { kind: "closed", mark: <MdRemove />, text: "已取消" };
    default:
      return { kind: "closed", mark: <MdRemove />, text: "未開始" };
  }
}

/* ===================== 頁面 ===================== */

function App(props) {
  const { id } = useParams();
  const projectId = Number(id);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { toast, confirmDelete, setDetailView } = useApp();

  /*
   * 各動作的權限。沒權限就不要放按鈕 —— 按了只會拿到 403，使用者會以為系統壞了。
   * 這裡只是不礙眼，真正的把關一律在後端的 [PermissionOnly]。
   * 會議紀錄走 project.*、里程碑走 schedule.*，跟後端 MeetingController /
   * PhaseController 掛的權限一字不差。
   */
  const canDeleteProject = hasPermission("project.delete");
  const canCreateMeeting = hasPermission("project.create");
  // project.edit：編輯專案、會議紀錄的編輯／刪除、成員的加入／移出都掛這個碼
  const canEditProject = hasPermission("project.edit");
  const canCreatePhase = hasPermission("schedule.create");
  const canEditPhase = hasPermission("schedule.edit");
  /* 匯入工項掛的是 task.create —— 匯入就是「一次新增很多筆」，
     跟後端 /Task/Import 的 [PermissionOnly("task.create")] 一字不差 */
  const canCreateTask = hasPermission("task.create");
  /*
   * 工項與文件（/Task/List、/Document/List）掛的是 task，沒有工項權限就不要問 ——
   * 問了只會拿到 403，對應的視圖也一併不顯示，比顯示一個永遠 0 筆的分頁誠實。
   * 里程碑（/Phase/List）後端已放寬成 schedule 或 project 皆可讀，所以不判斷。
   */
  const canSeeTask = useMemo(() => hasPermission("task"), []);
  /*
   * 「更多」展開區裡的新增工項／匯入要兩個權限都有。權限矩陣允許只勾工項「新增」不勾「檢視」，
   * 以前這兩顆只放在工項分頁、看不到工項分頁的人碰不到；現在五顆分頁都能展開「更多」，
   * 只看 task.create 就會放出去：新增導到 /Task/New（要 task）一定落在無權限頁，
   * 匯入雖然成功，匯進來的工項他自己卻看不到。
   */
  const canAddTask = canCreateTask && canSeeTask;
  /** 目前登入者帳號，給「只看：我負責的」用；取不到就不提供那個選項 */
  const me = useMemo(() => currentAccount(), []);

  /*
   * 送出中。值要留著用，不能只取 setter —— 送出鈕要 disable：
   * 新增一筆時後端還要同步寄通知信，慢的時候好幾秒，使用者以為沒反應就再按一次，
   * 於是同一筆被建立兩次。
   */
  const [loading, setLoading] = useState(false);
  /* 「工項回來了沒」要跟「一件都沒有」分開：兩者都是 0 筆，但一個該寫載入中、
     一個該寫空狀態。混在一起的話每次進頁面都會先閃一下「這個專案還沒有工項」 */
  const [tasksReady, setTasksReady] = useState(false);

  /* 子檢視：list（每一顆分頁都算）/ add（新增或編輯表單）。
     表單內容放不進網址，重新整理本來就留不住，所以留在頁內 state */
  /*
   * 新增／編輯表單開沒開，由網址上的 ?form=1 決定，不是元件自己的 state。
   *
   * 原本是 useState("list") → setView("add")，表單完全沒有進瀏覽器的歷史紀錄：
   * 在「新增里程碑」按上一頁（或滑鼠側鍵、手機的返回手勢），等於從整個專案詳情
   * 離開，直接掉回進來之前的專案列表，原本停在哪個分頁也一起丟了。
   * 開表單時推一筆 ?form=1 進歷史，上一頁就只是把它拿掉 —— 回到同一個分頁。
   *
   * 表單的內容（addCtx / editing / formValues）仍然是 state：它們不適合放進網址，
   * 重新整理之後也沒辦法還原，所以只有「開著」這件事放網址（見下面的 formOrphan）。
   */
  const formOpen = searchParams.get("form") === "1";
  /* 這一筆 ?form=1 是不是這一頁自己推進去的。是的話關表單用 navigate(-1) 退回去，
     歷史才不會多一格；不是的話（重新整理、或直接貼了帶 form=1 的網址）就用 replace 拿掉，
     否則 navigate(-1) 會把人帶離這一頁 */
  const pushedFormRef = useRef(false);
  /* 表單的內容還沒準備好（重新整理之後 state 全部歸零）。這時候畫不出一張有意義的表單 */
  const [formReady, setFormReady] = useState(false);
  const view = formOpen && formReady ? "add" : "list";
  const [addCtx, setAddCtx] = useState("phases");
  const [editing, setEditing] = useState(null); // null = 新增；物件 = 編輯中的紀錄
  const [formValues, setFormValues] = useState({});
  // 全系統帳號（新增成員用）；取不到就靜靜地退回專案成員
  const [adminPeople, setAdminPeople] = useState([]);

  /*
   * 「⋯ 更多」展開了沒，記在網址的 ?more=1，不是元件 state。
   *
   * 更多裡的「新增工項」「從 Excel 匯入工項」會換到別頁（/Task/New、/Project/Import），
   * 按上一頁回來時這一頁是重新掛載的 —— 記在 state 的話一回來就收起來了，
   * 使用者得再展開一次才接得上剛剛在看的東西。放網址的話上一頁、重新整理都留得住；
   * 換分頁（goTab 沿用整串 location.search）與開表單（?form=1 也是加在原網址上）也不會動到它，
   * 所以換分頁時不會自己收起或自己展開 —— 使用者展開了就是要看。
   * 不用 localStorage：那會變成「以後每個專案一進來都是展開的」，
   * 預設收合（讓分頁內容留在第一屏）就被某一次的操作悄悄改掉了。
   * 貼給別人的連結帶著 more=1，對方打開也是展開的 —— 他看到的就是貼的人看到的，可以接受。
   */
  const moreOpen = searchParams.get("more") === "1";
  /* 刪除專案的確認視窗（設計稿 L2630）。刪除不可逆，一定要先經過這一層 */
  const [askDelete, setAskDelete] = useState(false);

  const rawTab = parseListQuery(searchParams).tab;
  /* ?tab=board 不是這一頁的視圖（看板是另一頁）。真正的跳轉在下面的 effect，
     這一輪先當成工項分頁畫，不要讓分頁列變成一顆都沒選的樣子 */
  let tab = rawTab === TAB_BOARD ? TAB_ITEMS : rawTab;
  /*
   * 沒有工項權限的人，工項與文件兩顆分頁根本不放（兩支清單都掛 task 權限，fetchAll 沒去問）。
   * 預設網址、或別人貼來的 ?tab=docs 都落到里程碑，否則畫面上會是一片沒有分頁被選中的空白。
   */
  if (!canSeeTask && (tab === TAB_ITEMS || tab === TAB_DOCS)) tab = TAB_PHASES;

  /* ---------- 向後端拿東西 ---------- */

  const fetchAll = useCallback(() => {
    if (!projectId) return;
    props.GET_ProjectDetail({ ProjectId: projectId }, null, setLoading);
    if (canSeeTask) {
      props.GET_TaskList({ Page: 0, PageCount: 0, OrderKey: "id", OrderDirect: "ASC" }, projectId, null, (v) => {
        if (!v) setTasksReady(true);
      });
      props.GET_DocumentList(projectId, null, null);
    } else {
      setTasksReady(true);
    }
    props.GET_PhaseList(projectId, null, null);
    props.GET_MeetingList(projectId, null, null);
    props.GET_ProjectMembers({ ProjectId: projectId }, null, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, canSeeTask]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAll(); }, [id]);

  /* ---------- 後端回來的原始值（後端未完成時一律 fallback 空集合） ---------- */

  // 先取出來再進 useMemo，deps 才不會整包 props 都變成相依
  const detailProp = props.detail;
  const membersProp = props.members;
  const taskListProp = props.taskList;
  const phaseListProp = props.phaseList;
  const meetingListProp = props.meetingList;
  const documentsProp = props.documents;

  const project = useMemo(() => normalizeProject(detailProp || {}), [detailProp]);

  const rawTasks = useMemo(() => {
    if (Array.isArray(taskListProp)) return taskListProp;
    return _.get(taskListProp, "data_list", []) || [];
  }, [taskListProp]);

  /*
   * Task.List 是全站共用的一格 store：從別的專案切過來時，這裡讀到的還是上一個
   * 專案的工項，直到這一次的請求回來。帶得出 project_id 的就順手擋掉；
   * 帶不出來的（後端 Select 沒選這一欄）照收，否則清單會整個變空。
   */
  const all = useMemo(() => {
    const arr = rawTasks.map((t) => normalizeTask(t));
    return arr.some((t) => t.projectId != null) ? arr.filter((t) => Number(t.projectId) === projectId) : arr;
  }, [rawTasks, projectId]);

  const rawMembers = useMemo(() => {
    const arr = Array.isArray(membersProp) ? membersProp : _.get(membersProp, "data_list", null);
    if (arr && arr.length) return arr;
    return _.get(detailProp, "members", []) || [];
  }, [membersProp, detailProp]);

  const phaseList = useMemo(() => phaseListProp || [], [phaseListProp]);
  const meetingList = useMemo(() => meetingListProp || [], [meetingListProp]);
  const docList = useMemo(() => documentsProp || [], [documentsProp]);

  /* 今天的日界。用字串當依賴而不是 dayjs 物件：後者每次 render 都是新物件 */
  const todayKey = dayjs().format("YYYY-MM-DD");
  const today = useMemo(() => dayjs(todayKey).startOf("day"), [todayKey]);

  /* ---------- 成員 / 指派人對照 ---------- */

  const membersArr = useMemo(
    () =>
      rawMembers.map((m) => {
        const name = m.name || m.Name || m.account || m.Account || "";
        const account = m.account || m.Account || "";
        const owned = all.filter(
          (t) => (account && t.assigneeAccount === account) || (name && t.assignee === name)
        ).length;
        return {
          id: m.id ?? account ?? name,
          name,
          account,
          role: memberRoleLabel(m.role ?? m.Role),
          email: m.email || m.Email || "",
          tasks: owned,
        };
      }),
    [rawMembers, all]
  );

  /** 姓名 → 帳號（表單送出時要回填帳號給後端） */
  const accountOf = useCallback(
    (name) => {
      const hit = membersArr.find((m) => m.name === name);
      if (hit && hit.account) return hit.account;
      const adm = adminPeople.find((a) => a.name === name);
      if (adm && adm.account) return adm.account;
      const t = all.find((x) => x.assignee === name);
      return (t && t.assigneeAccount) || name || "";
    },
    [membersArr, adminPeople, all]
  );

  /** 帳號 → 姓名（文件上傳者等後端只回帳號的欄位） */
  const nameOf = useCallback(
    (account) => {
      if (!account) return "";
      const hit = membersArr.find((m) => m.account === account);
      if (hit && hit.name) return hit.name;
      const adm = adminPeople.find((a) => a.account === account);
      if (adm && adm.name) return adm.name;
      const t = all.find((x) => x.assigneeAccount === account);
      return (t && t.assignee) || account;
    },
    [membersArr, adminPeople, all]
  );

  /** 表單「出席人員」等下拉選項：優先用專案成員，拿不到成員名單時退回工項上的指派人 */
  const personNames = useMemo(() => {
    const names = membersArr.map((m) => m.name).filter(Boolean);
    if (names.length) return [...new Set(names)];
    return [...new Set(all.map((t) => t.assignee).filter(Boolean))];
  }, [membersArr, all]);

  /** 新增成員的帳號選項：全系統帳號扣掉已在專案內的人 */
  const memberCandidates = useMemo(() => {
    const taken = new Set(membersArr.map((m) => m.account).filter(Boolean));
    const names = adminPeople.filter((a) => !taken.has(a.account)).map((a) => a.name);
    return names.length ? [...new Set(names)] : personNames;
  }, [adminPeople, membersArr, personNames]);

  /* ---------- 里程碑 / 會議 / 文件（view-model） ---------- */

  const phasesArr = useMemo(
    () =>
      phaseList.map((p, i) => {
        const tc = Number(p.task_count ?? p.taskCount ?? 0);
        const dc = Number(p.completed_task_count ?? p.completedTaskCount ?? 0);
        /* 進度以底下的工項為準（後端也是這樣算的）；連工項數都沒有時才退回存起來的 progress */
        const pct = tc > 0 ? Math.round((dc / tc) * 100) : Number(p.progress ?? 0);
        return {
          id: p.id ?? i,
          no: Number(p.sort_order ?? p.sortOrder ?? 0) || i + 1,
          name: p.name || p.Name || "",
          desc: p.description || p.Description || "",
          start: fmtDay(p.start_date ?? p.startDate),
          end: fmtDay(p.end_date ?? p.endDate),
          startRaw: p.start_date ?? p.startDate,
          endRaw: p.end_date ?? p.endDate,
          statusKey: phaseStatusKey(p.status ?? p.Status),
          pct,
          items: tc,
          doneItems: dc,
          raw: p,
        };
      }),
    [phaseList]
  );

  /** 表單下拉的選項；EntityForm 的 select 收字串，所以用里程碑名稱當值 */
  const phaseNames = useMemo(() => phasesArr.map((p) => p.name).filter(Boolean), [phasesArr]);

  /** 里程碑 id → 名稱（清單列的第三格） */
  const phaseNameOf = useCallback(
    (pid) => {
      if (pid === null || pid === undefined || pid === "") return "";
      const hit = phasesArr.find((p) => Number(p.id) === Number(pid));
      return hit ? hit.name : "";
    },
    [phasesArr]
  );

  const meetingsArr = useMemo(
    () =>
      meetingList.map((m, i) => {
        const attendeesRaw = m.attendees || m.Attendees || "";
        const names = String(attendeesRaw)
          .split(/[、,，;；]/)
          .map((x) => x.trim())
          .filter(Boolean);
        const whenRaw = m.meeting_date ?? m.meetingDate;
        // decisions 現在是獨立欄位，不能再拿它當 content 的備援，否則會顯示兩次
        const notes = m.content || m.Content || "";
        return {
          id: m.id ?? i,
          title: m.title || m.Title || "",
          when: fmtMoment(whenRaw),
          whenRaw,
          attendees: names.map((n) => ({ name: n })),
          attendeesRaw,
          notes,
          /* 內容是編輯器產生的 HTML；只有空標籤（或整個空）時才當作沒填 */
          hasNotes: !!String(notes).replace(/<[^>]*>/g, "").trim() || /<img|<table/i.test(notes),
          location: m.location || m.Location || "",
          decisions: m.decisions || m.Decisions || "",
          fileName: m.file_name || m.fileName || "",
          fileSize: m.file_size ?? m.fileSize ?? null,
          hasFile: !!(m.has_file ?? m.hasFile ?? (m.file_name || m.fileName)),
          raw: m,
        };
      }),
    [meetingList]
  );

  const docsArr = useMemo(
    () =>
      docList.map((d, i) => {
        const who = nameOf(d.create_id || d.createId);
        return {
          id: d.id ?? i,
          name: d.file_name || d.fileName || d.name || "",
          cat: d.category || d.Category || "其他",
          /* 說明後端一直存得下也回得出來，舊清單只塞在 title 裡 —— 等於填了沒人看得到 */
          note: d.note || d.Note || "",
          noteText: toPlainText(d.note || d.Note || ""),
          size: fmtSize(d.file_size ?? d.fileSize),
          /* 上傳者或日期取不到就整段不寫（硬規則 10）：寧可少一格，也不要印「— 上傳」 */
          uploaded: [who, fmtDay(d.create_at ?? d.createAt)].filter(Boolean).join(" "),
        };
      }),
    [docList, nameOf]
  );

  /* ---------- 摘要卡要講的話（全部由後端回來的真實數字算） ---------- */

  const stats = useMemo(() => {
    const done = all.filter((t) => t.status === "done").length;
    const closed = all.filter((t) => t.status === "closed").length;
    const open = all.filter((t) => t.status !== "done" && t.status !== "closed");
    return {
      total: all.length,
      done,
      closed,
      remaining: open.length,
      dueToday: open.filter((t) => isSameDay(t.dueRaw, today)).length,
      /* 今天到期的不算逾期：due_date 存的是當天 00:00，用後端的 is_overdue 會讓
         今天要交的工項早上八點就被判成已經遲了（/Today 踩過的坑） */
      late: open.filter((t) => t.overdue && !isSameDay(t.dueRaw, today)).length,
      waiting: open.filter((t) => t.status === "review").length,
    };
  }, [all, today]);

  const headline = useMemo(() => {
    /* 摘要現在五顆分頁都展開得到，沒有工項權限的人也看得到這一句。
       他的工項清單根本沒去問（fetchAll），照下面算會變成「這個專案還沒有工項」—— 那是假話 */
    if (!canSeeTask) return "您的權限無法檢視工項，因此無法計算此專案的進度。";
    if (!tasksReady) return <InlineLoading text="工項載入中…" />;
    if (stats.total === 0) return "此專案尚無工項。";
    const parts = [];
    if (stats.late > 0) parts.push(`${stats.late} 件已逾期`);
    if (stats.dueToday > 0) parts.push(`今日有 ${stats.dueToday} 件到期`);
    if (stats.waiting > 0) parts.push(`${stats.waiting} 件待審核`);
    const head =
      `此專案共 ${stats.total} 件工項，已完成 ${stats.done} 件、尚餘 ${stats.remaining} 件` +
      (stats.closed > 0 ? `，另有 ${stats.closed} 件已關閉` : "");
    return parts.length > 0 ? `${head}；${parts.join("、")}。` : `${head}；今日無到期工項，亦無待審核工項。`;
  }, [canSeeTask, tasksReady, stats]);

  /** 「誰扛多少」—— 依 assignee 在前端分組，沒有這支 API */
  const workload = useMemo(() => {
    if (!tasksReady || stats.total === 0) return "";
    const byName = new Map();
    let nobody = 0;
    all.forEach((t) => {
      const n = t.assignee;
      if (!n) { nobody += 1; return; }
      byName.set(n, (byName.get(n) || 0) + 1);
    });
    const ranked = [...byName.entries()].sort((a, b) => b[1] - a[1]);
    const tail = nobody > 0 ? `另有 ${nobody} 件尚未指派負責人。` : "";
    if (ranked.length === 0) return tail;
    const [topName, topCount] = ranked[0];
    if (ranked.length === 1) {
      return nobody === 0
        ? `全部 ${topCount} 件皆由 ${topName} 負責。`
        : `${topCount} 件由 ${topName} 負責。${tail}`;
    }
    const rest = ranked.slice(1).reduce((s, [, n]) => s + n, 0);
    return `${topName} 負責最多，共 ${topCount} 件；其他 ${ranked.length - 1} 位合計 ${rest} 件。${tail}`;
  }, [tasksReady, stats.total, all]);

  const pct = Math.max(0, Math.min(100, Math.round(Number(project.pct) || 0)));

  /**
   * 數字自相矛盾要主動講出來（設計稿 L777-781）。
   *
   * 這個系統裡里程碑的進度是依底下的工項算的，所以「里程碑全部 0%、專案卻有進度」
   * 幾乎只有兩種原因：工項沒掛進里程碑，或掛進來的都還沒完成。
   * 不講的話使用者會以為是自己看錯，或以為系統把進度算丟了。
   *
   * 文案刻意不寫「到里程碑分頁把工項掛進去」：那一頁只能新增／編輯／刪除里程碑本身，
   * 沒有「把工項掛進來」的動作 —— 工項掛在哪個里程碑底下，是在工項自己的頁面選
   * 「屬於哪個里程碑」（resources/forms.js 的 phase 欄位）。分頁只負責讓人看每個里程碑
   * 底下現在各有幾件，指路就指到那裡為止，不要假裝那裡點得到。
   */
  const contradiction = useMemo(() => {
    if (phasesArr.length === 0 || pct <= 0) return null;
    if (phasesArr.some((p) => p.pct > 0)) return null;
    const noItems = phasesArr.every((p) => p.items === 0);
    return {
      title: "里程碑進度與專案進度不一致",
      text: noItems
        ? `${phasesArr.length} 個里程碑下皆尚無工項（因此皆顯示 0%），但專案整體進度已達 ${pct}%。里程碑進度依其下工項計算：請開啟工項並選擇所屬里程碑，進度才會更新；可選擇的里程碑列於下方「里程碑」分頁。`
        : `${phasesArr.length} 個里程碑的進度皆為 0%，但專案整體進度已達 ${pct}%。請至下方「里程碑」分頁檢視各里程碑的工項數與完成數；常見原因為歸入的工項皆尚未完成，或已完成的工項未選擇所屬里程碑。`,
    };
  }, [phasesArr, pct]);

  /* 舊「專案資訊」彈窗的內容。沒有值的欄位整列不放（硬規則 10） */
  const info = useMemo(() => {
    const rows = [];
    if (project.code) rows.push({ label: "專案代碼", value: project.code });
    if (project.client) rows.push({ label: "客戶", value: project.client });
    if (PROJ_STATUS_LABEL[project.status]) rows.push({ label: "狀態", value: PROJ_STATUS_LABEL[project.status] });
    if (PRIO_LABEL[project.prio]) rows.push({ label: "優先級", value: PRIO_LABEL[project.prio] });
    const s = fmtDay(project.startRaw);
    const e = fmtDay(project.endRaw);
    /* 起訖是後端由工項推導的（最早開始 ~ 最晚截止），不是誰填的 —— 標題就講明，
       免得有人到處找哪裡可以改 */
    if (s || e) rows.push({ label: "起訖（依工項推算）", value: `${s || "？"} – ${e || "？"}` });
    if (project.estimatedHours) rows.push({ label: "預估工時", value: `${project.estimatedHours} 小時` });
    const totalDays = project.startRaw && project.endRaw ? Math.max(0, daysBetween(project.startRaw, project.endRaw)) : 0;
    if (totalDays > 0) {
      const spent = Math.max(0, daysBetween(project.startRaw, new Date()));
      rows.push({ label: "已進行天數", value: `${Math.min(spent, totalDays)} / ${totalDays} 天` });
    }
    if (project.gitUrl) rows.push({ label: "程式碼位置", value: project.gitUrl, href: project.gitUrl });
    if (project.desc) rows.push({ label: "專案說明", value: project.desc });
    return rows;
  }, [project]);

  /** 標題底下那一句（設計稿 L764）。取不到的部分整段不寫 */
  const metaLine = useMemo(() => {
    const parts = [];
    const s = fmtDay(project.startRaw);
    if (s) parts.push(`${s} 開始`);
    const e = fmtDay(project.endRaw);
    if (e) parts.push(isSameDay(project.endRaw, today) ? `今日（${e}）到期` : `${e} 到期`);
    if (membersArr.length > 0) parts.push(`${membersArr.length} 位成員`);
    if (tasksReady && stats.total > 0) parts.push(`${stats.total} 件工項`);
    return parts.join(" · ");
  }, [project, today, membersArr.length, tasksReady, stats.total]);

  /* ---------- 分頁切換 ---------- */

  /**
   * 換分頁。用 replace：分頁不該在瀏覽器歷史裡一格一格留下來，
   * 按上一頁要能直接離開專案（跟舊版的行為一致）。
   * 順手把舊參數從網址上清掉，貼給別人的連結才不會又帶著一堆沒有作用的東西。
   * ?more=1 照樣留著：換分頁不該把使用者展開的「更多」收起來。
   * 看板已經不在分頁列裡（改成頁首的連結），這裡不再有導去 /Board 的分支。
   */
  const goTab = (next) => {
    const sp = new URLSearchParams(location.search);
    LEGACY_PARAMS.forEach((k) => sp.delete(k));
    if (next === TAB_ITEMS) sp.delete("tab");
    else sp.set("tab", next);
    const s = sp.toString();
    navigate(`${location.pathname}${s ? `?${s}` : ""}`, { replace: true });
  };

  /**
   * 展開／收合「⋯ 更多」。用 replace，理由跟換分頁一樣：這是同一頁上的顯示狀態，
   * 在歷史裡留一格的話，展開再收起之後要多按兩次上一頁才離得開專案。
   */
  const toggleMore = () => {
    const sp = new URLSearchParams(location.search);
    if (moreOpen) sp.delete("more");
    else sp.set("more", "1");
    const s = sp.toString();
    navigate(`${location.pathname}${s ? `?${s}` : ""}`, { replace: true });
  };

  /*
   * ?task= 的深連結（命令面板、通知、搜尋、儀表板都在產生）改成導到 /Task/:id 整頁。
   * 值可以是 id，也可以是工項代碼 —— 代碼要等這個專案的工項清單回來才查得到。
   * 查不到就把參數拿掉並說一聲，不要讓網址一直帶著一個無效的深連結。
   */
  const taskParam = searchParams.get("task");
  useEffect(() => {
    if (!taskParam) return;
    if (/^\d+$/.test(taskParam)) {
      navigate(`/Task/${taskParam}`, { replace: true });
      return;
    }
    if (!tasksReady) return;
    const hit = all.find((t) => t.code === taskParam);
    if (hit && hit.id != null) {
      navigate(`/Task/${hit.id}`, { replace: true });
      return;
    }
    const sp = new URLSearchParams(location.search);
    sp.delete("task");
    const s = sp.toString();
    navigate(`${location.pathname}${s ? `?${s}` : ""}`, { replace: true });
    toast(`此專案中找不到工項「${taskParam}」`, "error");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskParam, tasksReady, all]);

  /* 帶 ?tab=board 進來的連結（看板還是分頁那一代的書籤與貼出去的連結），
     意思是「這個專案的看板」—— 直接送去看板頁，不要停在這裡。
     用 replace：上一頁要回到進來之前的地方，而不是又落回這條會再跳一次的網址 */
  useEffect(() => {
    if (rawTab !== TAB_BOARD) return;
    /* 看板頁掛 task 權限（menu.js、BoardController 都是）。沒有工項權限的人送過去只會看到
       無權限頁，所以只把 tab 拿掉、留在這一頁（上面已經讓他落在里程碑分頁） */
    if (!canSeeTask) {
      const sp = new URLSearchParams(location.search);
      sp.delete("tab");
      const s = sp.toString();
      navigate(`${location.pathname}${s ? `?${s}` : ""}`, { replace: true });
      return;
    }
    navigate(`/Board?projectId=${projectId}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawTab, projectId, navigate, canSeeTask]);

  /*
   * 舊參數靜默清掉（見上面 LEGACY_PARAMS 的說明），舊的分頁鍵（TAB_ALIASES）也在這裡
   * 改寫成新鍵。兩件事併在同一個 effect、同一次 replace：分開寫的話一條舊連結
   * 會連跳兩次網址，而且兩個 effect 各自讀到的 location.search 是同一份舊值，
   * 後跑的那次會把先跑的改寫蓋回去。
   * 不報錯、不影響畫面（parseListQuery 這一輪已經照新鍵畫了），只是讓使用者接下來
   * 複製的網址不要再帶著沒有作用、或已經改名的東西。
   */
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const hit = LEGACY_PARAMS.filter((k) => sp.get(k) != null);
    const renamed = tabAlias(sp.get("tab"));
    if (hit.length === 0 && !renamed) return;
    hit.forEach((k) => sp.delete(k));
    if (renamed) sp.set("tab", renamed);
    const s = sp.toString();
    navigate(`${location.pathname}${s ? `?${s}` : ""}`, { replace: true });
  }, [location.search, location.pathname, navigate]);

  /*
   * 表單開著時網址換了（例如按上一頁）就把表單收掉 ——
   * 表單是頁內狀態，不收的話它會繼續蓋在清單的網址上。
   * 換網址也一併收掉刪除確認視窗（否則按上一頁之後，視窗會蓋在另一個畫面上）。
   * 「⋯ 更多」不在這裡收：它本身就記在網址上（?more=1），網址說展開就是展開。
   */
  useEffect(() => {
    setAskDelete(false);
    /*
     * 只在表單「已經關掉」時才清掉它的內容。
     * 打開表單本身也會換網址（推 ?form=1），這時候清掉的話，
     * 剛鋪好的預設值會在同一輪就被洗成空的。
     */
    if (!formOpen) {
      setEditing(null);
      // 已經是空的就不要再換一個新物件，免得每次換網址都多一輪重繪
      setFormValues((s) => (Object.keys(s).length ? {} : s));
      setFormReady(false);
      pushedFormRef.current = false;
    }
  }, [location.key, formOpen]);

  /*
   * 網址帶著 ?form=1、表單內容卻是空的（重新整理，或有人貼了這種網址）：
   * 這時候不知道要開哪一種表單，把參數拿掉回到清單，不要停在一張不知道在新增什麼的空白表單上。
   */
  useEffect(() => {
    if (!formOpen || formReady) return;
    const sp = new URLSearchParams(location.search);
    sp.delete("form");
    const q = sp.toString();
    navigate(`${location.pathname}${q ? `?${q}` : ""}`, { replace: true });
  }, [formOpen, formReady, location.search, location.pathname, navigate]);

  /* 刪除確認視窗要能用 Esc 關掉：鍵盤使用者要有一條不用找「取消」鈕的退路。
     （MUI Dialog 的 onClose 也會處理 Esc，這裡留著不影響；兩條路做的是同一件事）
     「⋯ 更多」不吃 Esc：它是頁面上展開的一段內容，不是蓋在頁面上的視窗 */
  useEffect(() => {
    if (!askDelete) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setAskDelete(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [askDelete]);

  /*
   * 回報給導覽用，格式是 resources/tours.js 的 tourSteps 認得的 "list:<分頁>"。
   * 分頁鍵本來就沿用舊五個分頁的字串（items / phases / docs / members / meetings），
   * 所以直接接上 tours.js 既有的五組步驟，不必再對照；看板不會停在這一頁，不會出現。
   * 導覽目前已下架、Layout 只留 setter 沒有人讀（見 Layout.js），這裡照樣回報，
   * 是為了導覽哪天接回來時這一頁不必再改。
   */
  const subView = view === "add" ? "add" : `list:${tab}`;
  useEffect(() => {
    setDetailView(subView);
  }, [subView, setDetailView]);

  /* ---------- 新增 / 編輯 ---------- */

  const openAdd = (ctx, record = null, values = {}) => {
    setAddCtx(ctx);
    setEditing(record);
    /*
     * 新增時才鋪預設值（開始日＝今天、結束日＝年底）。
     * 編輯時 values 是這一筆原本存的值，套預設會把使用者原本填的日期蓋掉。
     */
    setFormValues(record ? values : { ...formInitialValues(formConfig(ctx)), ...values });
    setFormReady(true);
    /* 推一筆 ?form=1 進歷史：上一頁回到這個分頁，而不是離開整個專案 */
    if (!formOpen) {
      const sp = new URLSearchParams(location.search);
      sp.set("form", "1");
      pushedFormRef.current = true;
      navigate(`${location.pathname}?${sp.toString()}`);
    }
    /*
     * 只有「新增成員」需要可選帳號清單。
     *
     * 打 /Project/Member/Candidates（掛 project 權限）而不是 /Auth/Admin/List（掛 admin）：
     * PM 與 RD主管有 project.edit、後端允許他們加人，卻拿不到 admin 專用的帳號清單，
     * 403 之後靜默退回專案現有成員，下拉就只剩自己一個人 —— 看起來像「沒有人可以加」。
     */
    if (ctx === "members" && !adminPeople.length) {
      apiGet("/Project/Member/Candidates", { projectId })
        .then((list) => {
          const arr = Array.isArray(list) ? list : _.get(list, "data_list", []) || [];
          setAdminPeople(
            arr
              .map((a) => ({
                name: a.name || a.Name || a.account || a.Account || "",
                account: a.account || a.Account || "",
              }))
              .filter((a) => a.name)
          );
        })
        .catch(() => {});
    }
  };

  const closeAdd = () => {
    /*
     * 自己推進去的就退回去，歷史才不會多一格 ——
     * 不然「取消」之後再按上一頁，會回到一個 ?form=1 的網址，而那時表單內容已經清掉了。
     * 不是自己推的（重新整理過）就用 replace 拿掉參數，navigate(-1) 會把人帶離這一頁。
     * 內容的清理交給上面那個跟著網址走的 effect，兩條路（按鈕、上一頁）只有一份收尾。
     */
    if (pushedFormRef.current) {
      pushedFormRef.current = false;
      navigate(-1);
      return;
    }
    const sp = new URLSearchParams(location.search);
    sp.delete("form");
    const q = sp.toString();
    navigate(`${location.pathname}${q ? `?${q}` : ""}`, { replace: true });
  };

  /* 新增工項走已經改版好的整頁表單（/Task/New），不在這一頁再養一份一模一樣的表單。
     帶著 projectId 過去，那一頁就不必再問一次「要加在哪個專案」。
     「更多」展開區與工項分頁清單下面各有一個入口，兩邊叫的是同一支 */
  const addTask = () => navigate(`/Task/New?projectId=${projectId}`);
  const importTasks = () => navigate(`/Project/Import/${projectId}`);

  const openEditProject = () =>
    openAdd("project", project, {
      code: project.code,
      name: project.name,
      // 起訖日已不在表單上（專案不管時間，由工項推導）
      git_url: project.gitUrl || "",
      description: project.desc,
    });

  const openEditPhase = (p) =>
    openAdd("phases", p, {
      name: p.name,
      start_date: fmtInputDate(p.startRaw),
      end_date: fmtInputDate(p.endRaw),
      description: p.desc,
    });

  const openEditMeeting = (m) => {
    // 後端存的是一個 datetime，表單拆成「日期」與「時間」兩格，這裡拆回去
    const d = m.whenRaw ? new Date(m.whenRaw) : null;
    const hh = d && !Number.isNaN(d.getTime()) ? String(d.getHours()).padStart(2, "0") : "";
    const mm = d && !Number.isNaN(d.getTime()) ? String(d.getMinutes()).padStart(2, "0") : "";
    openAdd("meetings", m, {
      title: m.title,
      meeting_date: fmtInputDate(m.whenRaw),
      time: hh ? `${hh}:${mm}` : "",
      attendees: m.attendeesRaw,
      location: m.location,
      decisions: m.decisions,
      content: m.notes,
      // 檔案不預填：FilePick 沒有 File 物件可用，改用欄位說明告知「不重選就保留原檔」
      file: null,
    });
  };

  const formFields = useMemo(() => {
    const base = formConfig(addCtx, addCtx === "members" ? memberCandidates : personNames, phaseNames);
    /*
     * 編輯會議紀錄且原本已有附件時，把檔名寫進欄位說明 ——
     * 否則使用者看到一個空的「選擇檔案」會以為附件不見了，或不確定不選會不會被清掉。
     * （後端 PutMeeting 只有在收到 file 時才覆寫，不選就保留原檔。）
     */
    if (addCtx === "meetings" && editing && editing.hasFile) {
      return base.map((f) =>
        f.key === "file"
          ? { ...f, hint: `目前的檔案：${editing.fileName || "（未命名）"}。未重新選擇時將保留原檔案。` }
          : f
      );
    }
    return base;
  }, [addCtx, personNames, memberCandidates, phaseNames, editing]);

  const addTitle = editing ? FORM_EDIT_TITLES[addCtx] || "編輯" : FORM_TITLES[addCtx] || "新增";

  const onFormChange = (key, val) => setFormValues((s) => ({ ...s, [key]: val }));

  const afterSubmit = (msg) => {
    closeAdd();
    fetchAll();
    toast(msg);
  };

  const submitProject = () => {
    const v = formValues;
    // 後端 PutProject 同樣是整筆覆蓋：進度、預估/實際工時、備註不在表單上，要帶回原值
    const r = _.get(project, "raw", {}) || {};
    props.PUT_Project(
      {
        ProjectId: projectId,
        Code: v.code || "",
        Name: v.name || "",
        /*
         * 客戶名稱／狀態／優先級不再送出（表單已移除這三格，見 forms.js）。
         * 「不送」跟「送預設值」是兩回事：後端收到 null 就不動這三欄，既有的值原封不動。
         * 也刻意不把讀到的狀態帶回去 —— 那是後端「算」出來的，寫回去等於把計算結果
         * 固化，之後再新增一筆未完成的工項，狀態就永遠卡在已完成了。
         *
         * 起訖日同理：帶回去等於把「推導出來的區間」寫死進 project 表，
         * 之後工項改了那筆快照就開始說謊。
         */
        StartDate: "",
        EndDate: "",
        EstimatedHours: r.estimated_hours ?? r.estimatedHours ?? null,
        ActualHours: r.actual_hours ?? r.actualHours ?? null,
        Progress: Number(r.progress ?? r.Progress ?? 0) || 0,
        Note: r.note ?? r.Note ?? null,
        GitUrl: v.git_url || "",
        Description: v.description || "",
      },
      () => afterSubmit("已更新專案"),
      setLoading
    );
  };

  const submitPhase = () => {
    const v = formValues;
    if (!String(v.name || "").trim()) { toast("請填寫里程碑名稱", "error"); return; }
    const fd = new FormData();
    fd.append("name", v.name);
    if (v.description) fd.append("description", v.description);
    if (v.start_date) fd.append("startDate", v.start_date);
    if (v.end_date) fd.append("endDate", v.end_date);
    fd.append("sortOrder", editing ? editing.no : phasesArr.length + 1);
    if (editing) {
      /*
       * status 一定要送（PutPhase 的簽章是必填 int），送的是這一筆目前的值。
       *
       * 注意：後端現在**不會**把它寫回去 —— PhaseService.PutPhase 一行都沒有碰
       * phase.Status，狀態改由底下的工項推導（見 PhaseService.PhaseList 的說明），
       * 那個參數只是為了不動 API 形狀才留著。所以這裡帶原值不是在保護狀態，
       * 純粹是不要送一個跟現況不符的數字出去；真的要改狀態要動的是後端。
       */
      const cur = Number(_.get(editing, "raw.status", _.get(editing, "raw.Status", 0))) || 0;
      fd.append("status", cur);
      props.PUT_Phase(editing.id, fd, () => afterSubmit("已更新里程碑"), setLoading);
    } else {
      props.POST_Phase(projectId, fd, () => afterSubmit("已建立里程碑"), setLoading);
    }
  };

  const submitMeeting = () => {
    const v = formValues;
    if (!String(v.title || "").trim()) { toast("請填寫會議主題", "error"); return; }
    // 後端是 DateTimeOffset.Parse(meetingDate)，空字串會直接丟例外冒成 400，
    // 在這裡先擋下來才講得出「要填什麼」
    if (!String(v.meeting_date || "").trim()) { toast("請選擇會議日期", "error"); return; }
    // 設計稿把日期與時間拆兩欄（時間是「14:00 - 15:00」自由字串），後端要一個 datetime
    const hhmm = String(v.time || "").match(/\d{1,2}:\d{2}/);
    const fd = new FormData();
    fd.append("title", v.title);
    fd.append("meetingDate", v.meeting_date ? `${v.meeting_date}T${hhmm ? hhmm[0] : "00:00"}` : "");
    // 出席人員與內容一律送（含空字串）：不送的話後端會把它們寫成 null，
    // 等於「清空出席名單」或「刪掉會議記錄」變成做不到的事
    fd.append("attendees", v.attendees || "");
    fd.append("content", v.content || "");
    fd.append("location", v.location || "");
    fd.append("decisions", v.decisions || "");
    // 沒重新挑檔案就不送 file，後端 PutMeeting 才會保留原本上傳的那一份
    if (v.file && v.file.name) fd.append("file", v.file);
    if (editing) {
      props.PUT_Meeting(editing.id, fd, () => afterSubmit("已更新會議紀錄"), setLoading);
    } else {
      props.POST_Meeting(projectId, fd, () => afterSubmit("已建立會議紀錄"), setLoading);
    }
  };

  const submitDoc = () => {
    const v = formValues;
    const file = v.file;
    if (!file) { toast("請選擇要上傳的檔案", "error"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", v.category || "其他");
    if (v.name) fd.append("name", String(v.name).trim());
    if (v.note) fd.append("note", v.note);
    props.POST_Document(projectId, fd, () => afterSubmit("已上傳文件"), setLoading);
  };

  const submitMember = () => {
    const v = formValues;
    if (!String(v.account || "").trim()) { toast("請選擇帳號", "error"); return; }
    props.POST_ProjectMember(
      {
        ProjectId: projectId,
        Account: accountOf(v.account) || v.account,
        Role: MEMBER_ROLE_CODE[v.role] ?? 1,
      },
      () => afterSubmit("已新增成員"),
      setLoading
    );
  };

  const onSubmitForm = () => {
    if (addCtx === "project") return submitProject();
    if (addCtx === "phases") return submitPhase();
    if (addCtx === "meetings") return submitMeeting();
    if (addCtx === "docs") return submitDoc();
    if (addCtx === "members") return submitMember();
    return undefined;
  };

  /* ---------- 刪除 ---------- */

  /**
   * 刪除專案。後端是軟刪（標記 is_deleted），底下的工項、里程碑、會議、文件、
   * 風險會因為全域查詢過濾器一起消失，但那些列都還留在 DB 裡，需要時 DBA 可以救回來。
   * 文案照實說「不會出現在系統中」而不是「永久刪除」，才不會誤導。
   */
  const deleteProject = async () => {
    setAskDelete(false);
    try {
      // DELETE /Project 吃 ?Data=<JWT>（後端 PayloadHelper.Decode<ReqGetProject>）
      await apiDelete("/Project", { Data: jwtEncode({ ProjectId: Number(projectId) }) });
      toast("已刪除專案");
      navigate("/Project/List");
    } catch (e) {
      toast(apiErrorMessage(e, "刪除失敗"), "error");
    }
  };

  /**
   * 刪除里程碑 —— 里程碑底下還有工項就不給刪。
   *
   * DB 的 fk_task_phase 是 ON DELETE SET NULL，硬刪其實刪得掉，
   * 但那些工項會安靜地變成「未分里程碑」，沒人會發現自己剛剛把一整段的
   * 分組資訊弄丟了。**後端的 DeletePhase 沒有這道檢查**（Detail.js 舊版的註解
   * 寫「後端也有同一道」是錯的），所以這一道拿掉就真的沒人擋了。
   */
  const deletePhase = (p) => {
    if (p.items > 0) {
      toast(`「${p.name}」下仍有 ${p.items} 個工項，請先刪除這些工項或將其移至其他里程碑，才能刪除此里程碑`, "error");
      return;
    }
    confirmDelete({
      title: "刪除里程碑？",
      subtitle: "此操作無法復原",
      body: `確定要刪除里程碑「${p.name}」嗎？此里程碑目前沒有工項。`,
      onConfirm: () => props.DELETE_Phase(p.id, () => { toast("已刪除里程碑"); fetchAll(); }, setLoading),
    });
  };

  const deleteMeeting = (m) =>
    confirmDelete({
      title: "刪除會議紀錄？",
      subtitle: "此操作無法復原",
      body:
        `確定要刪除「${m.title}」嗎？` +
        (m.hasFile ? `\n已上傳的檔案「${m.fileName}」也將一併從伺服器移除。` : ""),
      onConfirm: () =>
        props.DELETE_Meeting(m.id, () => { toast("已刪除會議紀錄"); fetchAll(); }, setLoading),
    });

  /**
   * 把成員移出專案。
   *
   * 只是取消參與，不會動到他已經建立或被指派的工項 —— 那些工項仍掛在他名下，
   * 只是他可能因為資料範圍是「所屬專案」而看不到了。這件事要在對話框裡講清楚，
   * 否則 PM 會以為移除成員等於把他的工作一併清掉。
   */
  const removeMember = (m) =>
    confirmDelete({
      title: "移出專案？",
      subtitle: "只取消參與，不影響既有工項",
      confirmText: "移出",
      body:
        `確定要將「${m.name}」移出此專案嗎？\n` +
        (m.tasks > 0
          ? `該使用者名下的 ${m.tasks} 個工項不會被刪除，仍由該使用者負責；但若其角色資料範圍為「所屬專案」，移出後將無法檢視這些工項。\n`
          : "") +
        `日後可透過「新增成員」重新加入。`,
      onConfirm: () =>
        props.DELETE_ProjectMember(
          { MemberId: Number(m.id) },
          () => { toast(`已將 ${m.name} 移出專案`); fetchAll(); },
          setLoading
        ),
    });

  const deleteDoc = (d) =>
    confirmDelete({
      title: "刪除文件？",
      subtitle: "此操作無法復原",
      body: `確定要刪除「${d.name}」嗎？`,
      onConfirm: () => props.DELETE_Document(d.id, () => { toast("已刪除文件"); fetchAll(); }, setLoading),
    });

  /* ---------- 下載（端點有 [Authorize]，直接開 <a href> 不會帶 Bearer，一定 401） ---------- */

  const download = async (url, filename, failMsg) => {
    try {
      const blob = await request.get(url, null, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = filename || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      toast(apiErrorMessage(err, failMsg), "error");
    }
  };

  const downloadDoc = (d) => download(`/Document/Download/${d.id}`, d.name, "下載失敗");
  const downloadMeetingFile = (m) => download(`/Meeting/Download/${m.id}`, m.fileName || "meeting", "下載失敗");

  /* ---------- 畫面 ---------- */

  /*
   * 表單是自己的一頁（EntityForm 自帶標題、返回鍵與整頁唯一的主鈕）。
   * 表單開著時不畫底下的摘要卡與清單 —— 兩顆主鈕同時在畫面上
   * 就違反硬規則 1，而且使用者會不知道「建立」是要建立哪一個東西。
   */
  if (view === "add") {
    return (
      <EntityForm
        title={addTitle}
        fields={formFields}
        values={formValues}
        onChange={onFormChange}
        onBack={closeAdd}
        onSubmit={onSubmitForm}
        submitText={editing ? "儲存" : "建立"}
        busy={loading}
      />
    );
  }

  const tag = projectTag(project, today);

  /*
   * 分頁列的內容。工項在前（每天在看的），四區接在後面。
   * 看板原本排在工項後面，但它按下去是換到另一頁，不是原地切換 —— 放在分頁列裡，
   * 使用者會以為它跟其他幾顆一樣，按了才發現整頁換掉、分頁列也不見了。
   * 所以它搬到頁首第一行的最右邊，寫成一個看得出「去別頁」的連結。
   *
   * 件數 0 的分頁照樣放、只是不寫「0」（同 MyTask/Index.js 的分頁，設計稿 L1114）：
   *   · 照樣放 —— 還沒有里程碑／成員的專案，新增第一筆的入口就在那一頁的空狀態裡，
   *     分頁藏起來等於把「加第一筆」的路一起藏掉。
   *   · 不寫 0 —— 一排「成員 0、文件 0、會議紀錄 0」只是在用數字講「這裡沒東西」，
   *     有數字的分頁才跳得出來。MyTask 那句「也不用去點」在這裡刻意不抄：這裡的空分頁
   *     是要點進去新增的。
   * 工項數在清單回來之前也是 0，所以載入中不會先閃一個「工項 0」。
   *
   * 沒有工項權限的人不放「工項」與「文件」：兩支清單都掛 task 權限、fetchAll 根本沒去問，
   * 放了就是一個永遠空白的分頁，比不放更像東西掉了。
   */
  const tabs = [
    canSeeTask && { key: TAB_ITEMS, label: "工項", count: stats.total },
    { key: TAB_PHASES, label: "里程碑", count: phasesArr.length },
    { key: TAB_MEMBERS, label: "成員", count: membersArr.length },
    canSeeTask && { key: TAB_DOCS, label: "文件", count: docsArr.length },
    { key: TAB_MEETINGS, label: "會議紀錄", count: meetingsArr.length },
  ].filter(Boolean);

  /*
   * 「⋯ 更多」旁邊那句說明：照這個人真的會在裡面看到的東西寫。
   * 沒有新增工項或刪除專案權限的人，裡面就沒有那兩顆鈕 —— 說明寫了卻找不到，
   * 使用者會以為是自己眼花，或以為按鈕壞了。
   */
  const moreParts = [canSeeTask ? "進度" : "專案摘要"];
  if (info.length > 0) moreParts.push("專案資訊");
  if (canAddTask) moreParts.push("新增工項");
  if (canEditProject && canDeleteProject) moreParts.push("編輯與刪除專案");
  else if (canEditProject) moreParts.push("編輯專案");
  else if (canDeleteProject) moreParts.push("刪除專案");
  const moreHint = `${moreParts.join("、")}${moreParts.length > 1 ? "皆" : ""}位於「更多」`;

  return (
    <>
      {/*
        頁首。五顆分頁一字不差，不再用 tab === TAB_ITEMS 切換頁首的內容（理由見檔頭）。

        第一行：左邊標題＋狀態、右邊「打開看板」。
        讓位的是標題：專案名稱二三十個字、手機寬度加大字模式時，標題多換幾行，
        看板鈕不縮、不換行，不會被擠出畫面。
      */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
        <Stack
          direction="row"
          spacing={1.5}
          useFlexGap
          sx={{ flexWrap: "wrap", alignItems: "center", flex: "1 1 auto", minWidth: 0 }}
        >
          {/* 標題不跟著分頁加後綴：被選中的分頁本身就標得出現在在哪一區，
              標題跟著換字反而像是換了一頁 */}
          <Typography variant="h1" component="h1" sx={{ overflowWrap: "anywhere" }}>
            {project.name || "專案"}
          </Typography>
          {/* 標題旁邊只講這個專案這一刻最該知道的一件事（設計稿 L762） */}
          <StatusChip kind={tag.kind} mark={tag.mark}>
            {tag.text}
          </StatusChip>
        </Stack>
        {/*
          看板是另一頁（/Board），所以是連結、不是 role="tab"。
          寫成 Link 而不是按鈕 + navigate：它本來就是去別頁，
          中鍵、右鍵「在新分頁開啟」都要能用，輔助科技也會唸成「連結」。
          字寫「打開看板」而不是只寫「看板」，後面的 › 跟清單列右邊的 › 同一個意思：點了會走到別處。
          outlined 而不是 contained：頁首不放主鈕（硬規則 1，見下面「更多」的說明）。
          沒有工項權限的人不放：/Board 掛 task 權限，按了只會落在無權限頁。
        */}
        {canSeeTask ? (
          <Button
            variant="outlined"
            component={Link}
            to={`/Board?projectId=${projectId}`}
            endIcon={<MdChevronRight aria-hidden="true" />}
            sx={{ flex: "0 0 auto" }}
          >
            開啟看板
          </Button>
        ) : null}
      </Box>

      {metaLine ? <Typography sx={{ mt: 1.5 }}>{metaLine}</Typography> : null}

      {/*
        「⋯ 更多」：原地展開／收合，不是彈窗。五顆分頁都有、展開的內容也都一樣 ——
        它講的是整個專案（進度、誰扛多少、專案資訊、編輯與刪除），不是某一顆分頁。
        沒有工項權限、連工項分頁都看不到的人，編輯與刪除專案也靠它（硬規則 8：功能不能走不到）。

        以前摘要卡只擺在工項分頁、而且是自動擋在清單前面，理由是「放在成員或會議紀錄上面是答非所問」。
        現在它預設收著、是使用者自己按了才展開，那個理由就不成立了：
        想在會議紀錄分頁順手看一眼進度的人，按一下就看得到，不必先切回工項。

        aria-expanded / aria-controls 讓輔助科技唸得出「已展開／已收合」；
        ▾ ▴ 是給眼睛看的同一件事，所以 aria-hidden，按鈕的名字固定是「更多」不跟著換。
      */}
      <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center", mt: 2 }}>
        <Button
          variant="outlined"
          type="button"
          aria-expanded={moreOpen}
          aria-controls="pd-more"
          onClick={toggleMore}
          startIcon={<MdMoreHoriz aria-hidden="true" />}
          endIcon={<Box component="span" aria-hidden="true" sx={{ display: "inline-flex" }}>{moreOpen ? <MdExpandLess /> : <MdExpandMore />}</Box>}
        >
          更多
        </Button>
        <Typography variant="body2" color="text.secondary" component="span">
          {moreHint}
        </Typography>
      </Stack>

      {/*
        展開區用 hidden 收起而不是整塊不畫：裡面「專案資訊」收合區開到一半、收起來再展開，
        要停在原本的樣子。hidden 掛在不帶樣式的外層 div 上 —— 掛在有 display 樣式的元件上，
        hidden 就會被蓋掉。
      */}
      <div id="pd-more" hidden={!moreOpen}>
        <DetailSummary
          headline={headline}
          workload={workload}
          pct={pct}
          showProgress={tasksReady && stats.total > 0}
          /* 進度條的顏色（Progress 的 tone）：做完了就綠、期限已經出事就紅，其餘用主色 */
          progressTone={pct >= 100 || project.status === "done" ? "done" : tag.kind === "attention" ? "attention" : "progress"}
          /* 沒有工項權限的人不放矛盾警示：那段話叫人「打開工項、選里程碑」，
             而他打不開工項 —— 指一條他走不到的路，比不講更糟 */
          contradiction={canSeeTask ? contradiction : null}
          info={info}
          canCreateTask={canAddTask}
          canEditProject={canEditProject}
          canDeleteProject={canDeleteProject}
          onAddTask={addTask}
          onImport={importTasks}
          onEditProject={openEditProject}
          /* 只是打開確認視窗，不會一按就刪 */
          onDeleteProject={() => setAskDelete(true)}
        />
      </div>

      {/*
        分頁列（設計稿 L791；顆數見上面 tabs 的說明）。MUI Tabs 自帶 role="tablist" / "tab"
        與 aria-selected，輔助科技讀得出哪一顆被選中。
        這裡的每一顆都是原地切換；會換頁的看板已經搬到頁首（見上面），不再混在這一排裡。

        五顆在手機寬度一排放不下：用 scrollable 加左右捲動鈕（手機也顯示），
        不出現捲軸，藏在右邊的分頁也有箭頭指出來，不會被推出畫面外沒人發現。
      */}
      <Tabs
        value={tab}
        onChange={(_e, next) => goTab(next)}
        variant="scrollable"
        allowScrollButtonsMobile
        aria-label="專案內容"
        sx={{ mt: 4, borderBottom: 1, borderColor: "divider" }}
      >
        {tabs.map((t) => (
          <Tab
            key={t.key}
            value={t.key}
            id={`pd-tab-${t.key}`}
            aria-controls="pd-panel"
            label={t.count > 0 ? `${t.label} ${t.count}` : t.label}
          />
        ))}
      </Tabs>

      <Box id="pd-panel" role="tabpanel" aria-labelledby={`pd-tab-${tab}`} sx={{ mt: 3 }}>
        {tab === TAB_ITEMS ? (
          <DetailItems
            tasks={all}
            ready={tasksReady}
            me={me}
            canCreateTask={canCreateTask}
            onAddTask={addTask}
            onImport={importTasks}
            phaseNameOf={phaseNameOf}
            /* 工項詳情是自己的一頁（/Task/:id）：按上一頁回得到這裡，
               連結也貼得出去，不再是一個只有本頁才打得開的頁內面板 */
            onOpenTask={(t) => {
              if (t.id != null) navigate(`/Task/${t.id}`);
            }}
          />
        ) : (
          <DetailData
            /* 分頁鍵與 DetailData 的 section 是同一組字串（見 TAB_* 的說明），直接傳 */
            section={tab}
            phases={phasesArr}
            docs={docsArr}
            members={membersArr}
            meetings={meetingsArr}
            projectPct={pct}
            canCreatePhase={canCreatePhase}
            canEditPhase={canEditPhase}
            canSeeTask={canSeeTask}
            canCreateMeeting={canCreateMeeting}
            canEditProject={canEditProject}
            onAddPhase={() => openAdd("phases")}
            onEditPhase={openEditPhase}
            onDeletePhase={deletePhase}
            onAddDoc={() => openAdd("docs")}
            onDownloadDoc={downloadDoc}
            onDeleteDoc={deleteDoc}
            onAddMember={() => openAdd("members")}
            onRemoveMember={removeMember}
            onAddMeeting={() => openAdd("meetings")}
            onEditMeeting={openEditMeeting}
            onDeleteMeeting={deleteMeeting}
            onDownloadMeetingFile={downloadMeetingFile}
          />
        )}
      </Box>

      {/*
        毀損性操作一律先經過確認視窗（設計稿 L2629-L2639）。後果寫成整句，不是灰字小字。
        原本的「⋯ 更多」彈窗已經拿掉（它的內容搬進上面的展開區），這一層留著：刪除不可逆。
        MUI Dialog 自己處理層級（蓋得過手機版側欄抽屜）與窄螢幕縮寬。
      */}
      <Dialog
        open={askDelete}
        onClose={() => setAskDelete(false)}
        maxWidth="sm"
        fullWidth
        role="alertdialog"
        aria-labelledby="pd-del-title"
      >
        <DialogTitle id="pd-del-title" component="h2">
          確定要刪除「{project.name || "此專案"}」嗎？
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>此專案下的工項、里程碑、會議紀錄、文件與風險將不再顯示於系統中。</Typography>
          <Banner kind="warn" icon={<MdErrorOutline />}>
            <strong>刪除後無法自行復原　</strong>
            資料仍保留於資料庫中，如需復原請聯絡系統管理員。
          </Banner>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button variant="outlined" type="button" onClick={() => setAskDelete(false)}>
            取消
          </Button>
          <Button variant="contained" color="error" type="button" onClick={deleteProject}>
            確定刪除
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

const mapStateToProps = (state) => ({
  detail: _.get(state, "Project.Detail", {}),
  members: _.get(state, "Project.Members", []),
  taskList: _.get(state, "Task.List", {}),
  phaseList: _.get(state, "Phase.List", []),
  meetingList: _.get(state, "Meeting.List", []),
  documents: _.get(state, "Document.List", []),
});

/*
 * 19 支 dispatch 一支都沒有改。
 *
 * POST_Task / PUT_Task 現在這一頁沒有呼叫端（工項的新增走 /Task/New、編輯走 /Task/:id，
 * 兩頁都是改版後的整頁表單，不必在專案詳情裡再養一份一模一樣的）。
 * 接線仍然留著：之後若要把「在專案裡直接開一件工項」做回頁內表單，接回去就能用，
 * 也免得別人以為這兩支 action 已經不存在了。
 */
const mapDispatchToProps = (dispatch) => ({
  GET_ProjectDetail: (payload, callback, loading) => dispatch({ type: "GET_ProjectDetail", payload, callback, loading }),
  GET_ProjectMembers: (payload, callback, loading) => dispatch({ type: "GET_ProjectMembers", payload, callback, loading }),
  GET_TaskList: (payload, projectId, callback, loading) => dispatch({ type: "GET_TaskList", payload, projectId, callback, loading }),
  GET_PhaseList: (projectId, callback, loading) => dispatch({ type: "GET_PhaseList", projectId, callback, loading }),
  GET_MeetingList: (projectId, callback, loading) => dispatch({ type: "GET_MeetingList", projectId, callback, loading }),
  GET_DocumentList: (projectId, callback, loading) => dispatch({ type: "GET_DocumentList", projectId, callback, loading }),
  POST_Task: (payload, callback, loading) => dispatch({ type: "POST_Task", payload, callback, loading }),
  PUT_Task: (payload, callback, loading) => dispatch({ type: "PUT_Task", payload, callback, loading }),
  PUT_Project: (payload, callback, loading) => dispatch({ type: "PUT_Project", payload, callback, loading }),
  POST_Phase: (projectId, formData, callback, loading) => dispatch({ type: "POST_Phase", projectId, formData, callback, loading }),
  PUT_Phase: (phaseId, formData, callback, loading) => dispatch({ type: "PUT_Phase", phaseId, formData, callback, loading }),
  DELETE_Phase: (phaseId, callback, loading) => dispatch({ type: "DELETE_Phase", phaseId, callback, loading }),
  POST_Meeting: (projectId, formData, callback, loading) => dispatch({ type: "POST_Meeting", projectId, formData, callback, loading }),
  PUT_Meeting: (meetingId, formData, callback, loading) => dispatch({ type: "PUT_Meeting", meetingId, formData, callback, loading }),
  DELETE_Meeting: (meetingId, callback, loading) => dispatch({ type: "DELETE_Meeting", meetingId, callback, loading }),
  POST_Document: (projectId, formData, callback, loading) => dispatch({ type: "POST_Document", projectId, formData, callback, loading }),
  DELETE_Document: (documentId, callback, loading) => dispatch({ type: "DELETE_Document", documentId, callback, loading }),
  POST_ProjectMember: (payload, callback, loading) => dispatch({ type: "POST_ProjectMember", payload, callback, loading }),
  DELETE_ProjectMember: (payload, callback, loading) => dispatch({ type: "DELETE_ProjectMember", payload, callback, loading }),
});

App.propTypes = {
  detail: PropTypes.object,
  members: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  taskList: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  phaseList: PropTypes.array,
  meetingList: PropTypes.array,
  documents: PropTypes.array,
  GET_ProjectDetail: PropTypes.func,
  GET_ProjectMembers: PropTypes.func,
  GET_TaskList: PropTypes.func,
  GET_PhaseList: PropTypes.func,
  GET_MeetingList: PropTypes.func,
  GET_DocumentList: PropTypes.func,
  POST_Task: PropTypes.func,
  PUT_Task: PropTypes.func,
  PUT_Project: PropTypes.func,
  POST_Phase: PropTypes.func,
  PUT_Phase: PropTypes.func,
  DELETE_Phase: PropTypes.func,
  POST_Meeting: PropTypes.func,
  PUT_Meeting: PropTypes.func,
  DELETE_Meeting: PropTypes.func,
  POST_Document: PropTypes.func,
  DELETE_Document: PropTypes.func,
  POST_ProjectMember: PropTypes.func,
  DELETE_ProjectMember: PropTypes.func,
};

App.defaultProps = {
  detail: {},
  members: [],
  taskList: {},
  phaseList: [],
  meetingList: [],
  documents: [],
  GET_ProjectDetail: () => {},
  GET_ProjectMembers: () => {},
  GET_TaskList: () => {},
  GET_PhaseList: () => {},
  GET_MeetingList: () => {},
  GET_DocumentList: () => {},
  POST_Task: () => {},
  PUT_Task: () => {},
  PUT_Project: () => {},
  POST_Phase: () => {},
  PUT_Phase: () => {},
  DELETE_Phase: () => {},
  POST_Meeting: () => {},
  PUT_Meeting: () => {},
  DELETE_Meeting: () => {},
  POST_Document: () => {},
  DELETE_Document: () => {},
  POST_ProjectMember: () => {},
  DELETE_ProjectMember: () => {},
};

/*
 * 換專案（例如從命令面板直接跳到另一個專案）時整個重掛。
 * 路由元素相同，React Router 不會自己重建元件 —— 不重掛的話上一個專案的視圖、
 * 篩選、表單狀態會原封不動套到新專案上。
 */
function ProjectDetailRoute(props) {
  const { id } = useParams();
  return <App key={id} {...props} />;
}

export default connect(mapStateToProps, mapDispatchToProps)(ProjectDetailRoute);

/* ===================== 暫不使用（跟著磁磚版文件清單一起收起來的） =====================
 *
 * 硬規則 8：移除顯示時，邏輯與常數留在檔案內並註明。
 * 舊的文件分頁是方形磁磚，每一張左上角有一個三字副檔名徽章（PDF／DOC／XLS…）
 * 配一組顏色。現在文件是清單裡的一列，列上沒有圖示的位置（清單列只有
 * 標題、meta 與右側動作），分類直接寫在 meta 裡，所以這組配色暫時沒有用到。
 *
 * DOC_CATS 是上傳表單 resources/forms.js 的 category 選項副本，舊版拿它排分類頁籤；
 * 現在「只看」的選項直接由這個專案真的有的文件分類推導（沒有那一類就不會出現在下拉裡），
 * 所以也不再需要 —— 但兩邊要對得上這件事沒有變，改 forms.js 時記得回頭看一眼。
 * ============================================================================ */

/* eslint-disable no-unused-vars */

/** 設計稿 docsData 內建三種配色，其餘副檔名沿用同語彙擴充 */
const DOC_ICON = {
  PDF: { bg: "rgba(217,45,32,.12)", color: "#d92d20" },
  FIG: { bg: "rgba(124,92,255,.14)", color: "#7c5cff" },
  DOC: { bg: "rgba(37,99,235,.12)", color: "#2563eb" },
  XLS: { bg: "rgba(14,124,102,.12)", color: "#0e7c66" },
  IMG: { bg: "rgba(220,104,3,.12)", color: "#dc6803" },
  PPT: { bg: "rgba(217,75,143,.12)", color: "#d94b8f" },
};

/** 副檔名 → 三字徽章文字 */
function extOf(fileName) {
  const raw = String(fileName || "").split(".").pop().toLowerCase();
  if (["pdf"].includes(raw)) return "PDF";
  if (["doc", "docx"].includes(raw)) return "DOC";
  if (["xls", "xlsx", "csv"].includes(raw)) return "XLS";
  if (["ppt", "pptx"].includes(raw)) return "PPT";
  if (["fig", "sketch", "xd"].includes(raw)) return "FIG";
  if (["png", "jpg", "jpeg", "gif", "svg", "webp"].includes(raw)) return "IMG";
  return raw ? raw.slice(0, 3).toUpperCase() : "FILE";
}

/**
 * 文件分類 —— 與上傳表單 resources/forms.js 的 category 選項一字不差。
 * 兩邊對不上的話，會出現一個永遠是 0 的分類，或有文件被分到不存在的分類裡。
 */
const DOC_CATS = ["需求", "設計稿", "會議記錄", "合約", "其他"];

/* eslint-enable no-unused-vars */
